import { Account } from 'pages/Account'
import { ApolloProvider } from 'react-apollo'
import {
  AppShell,
  BinocularsIcon,
  BookIcon,
  Button,
  CertificateIcon,
  ChartAreaIcon,
  ExternalProviderWrappers,
  HandshakeIcon,
  Redirect,
  Route,
  Switch,
  Typography,
  UserIcon,
  UserPlusIcon,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { CampaignFormPage } from 'pages/CampaignFormPage'
import { Campaigns } from 'pages/Campaigns'
import { Dashboard } from 'pages/Dashboard'
import { Issuer } from 'pages/Issuer'
import { NavigatorItem } from 'isolated-design-library'
import { Opportunity } from 'pages/Opportunity'
import { OrderBook } from 'pages/OrderBook'
import { ScrollToTopOnRouteChange } from 'isolated-design-library/components/ScrollToTopOnRouteChange'
import { WorkInProgress } from 'pages/WorkInProgress'
import Loading from 'components/shared/Loading'
import ProfileNavbarButton from 'components/shared/ProfileNavbarButton'
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'

import { Accounts } from './pages/Accounts'
import { AdminClient } from './graphql/clients'
import { Campaign } from 'pages/Campaign'
import { CreateSegmentFormPage, UpdateSegmentFormPage } from 'pages/SegmentFormPage'
import { InvestorLeads } from './pages/InvestorLeads'
import { LookerProofOfConcept } from './pages/LookerProofOfConcept'
import { OmniSearch } from './components/field-renderers/OmniSearch'
import { QuickAddButton } from './components/shared/QuickAddButton'
import { Routes } from './util/Routes'
import { Segments } from './pages/Segments'
import { useAppDataQuery, useClearErrorsMutation, useErrorsQuery } from './graphql/bindings/AdminBindings'
import { version as versionAtBuild } from 'util/Version'
import Analytics from './util/Analytics'
import EnvVars from 'util/EnvVars'
import ErrorBoundary from './components/shared/ErrorBoundary'
import useVersionPolling from 'hooks/useVersionPolling'

const Deals = lazy(() => import(/* webpackChunkName: "deals" */ './pages/Deals'))

const ErrorWatcher: React.FC = () => {
  const { data } = useErrorsQuery()
  const [clearErrors] = useClearErrorsMutation()
  const { enqueueSnackbar } = useSnackbar()
  const errors = data?.errors || []
  useEffect(() => {
    if (errors) {
      errors.forEach(error => {
        enqueueSnackbar(`ERROR: ${error}`, { variant: 'error' })
      })
      clearErrors()
    }
  }, [errors, enqueueSnackbar, clearErrors])
  return null
}

const VersionWatcher: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const messageShown = useRef(false)

  useVersionPolling(
    () => {
      if (!messageShown.current) {
        enqueueSnackbar(
          'There is a new version of Market Console available. Please commit any unsaved work and then refresh the page.',
          {
            persist: true,
            action: (
              <Button size="small" variant="text" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            ),
          }
        )

        messageShown.current = true
      }
    },
    {
      enabled: process.env.NODE_ENV !== 'development',
      initialVersion: versionAtBuild,
    }
  )

  return null
}

// This is ugly, but allows us to test this flow with Cypress.
declare global {
  interface Window {
    __SET_LOCATION_HREF__: (url: string) => void
  }
}
const setLocationHref = (url: string) => (window.location.href = url)
window.__SET_LOCATION_HREF__ = window.__SET_LOCATION_HREF__ || setLocationHref

const AppDataLoading: React.FC = () => null
const Unauthorized: React.FC = () => {
  const currentUrl = window.location.href
  const loginUrl = `${EnvVars.PLATFORM_URL}/login?redirect=${currentUrl}`
  const [showMessage, setShowMessage] = useState(false)

  useEffect(() => {
    const setLocation = window.__SET_LOCATION_HREF__
    if (typeof setLocation === 'function') setLocation(loginUrl)
  }, [loginUrl])

  useEffect(() => {
    const timeoutId = setTimeout(() => setShowMessage(true), 200)
    return () => clearTimeout(timeoutId)
  }, [setShowMessage])

  return showMessage ? (
    <Typography>
      You are not authorized to access this app and may need to log in. Click <a href={loginUrl}>here</a> if you are not
      redirected.
    </Typography>
  ) : null
}
const NotAdmin: React.FC = () => (
  <Typography>You must be an admin user with 2FA enabled to access this page.</Typography>
)
const UnknownError: React.FC = () => <Typography>Something went wrong. Please try refreshing the page.</Typography>

const UNAUTHORIZED_SERVER_MESSAGE = 'Unauthorized'
const NOT_AN_ADMIN_SERVER_MESSAGE = 'Not an admin'

const App: React.FC = () => {
  const { data: appData, loading: appDataLoading, error: appDataError } = useAppDataQuery()
  const currentAdmin = appData?.currentAdmin
  const isUnauthorized = !!appDataError && appDataError.message.includes(UNAUTHORIZED_SERVER_MESSAGE)
  const isNotAdmin = !!appDataError && appDataError.message.includes(NOT_AN_ADMIN_SERVER_MESSAGE)

  useEffect(() => {
    Analytics.injectLogRocket()
  }, [])

  useEffect(() => {
    if (currentAdmin) Analytics.identify(currentAdmin)
  }, [currentAdmin])

  const headerItems = <ProfileNavbarButton />

  const navigatorItems: NavigatorItem[] = [
    { id: 'Dashboard', to: Routes.home(), icon: <ChartAreaIcon />, exact: true },
    { id: 'Active Deals', to: Routes.deals(), icon: <HandshakeIcon /> },
    { id: 'Accounts', to: Routes.accounts(), icon: <UserIcon /> },
    { id: 'Order Book', to: Routes.orderBook(), icon: <BookIcon /> },
    { id: 'Campaigns', to: Routes.campaigns(), icon: <CertificateIcon /> },
    { id: 'Investor Leads', to: Routes.investorLeads(), icon: <BinocularsIcon /> },
    { id: 'Segments', to: Routes.segments(), icon: <UserPlusIcon /> },
  ]

  if (appDataLoading) return <AppDataLoading />
  if (isUnauthorized) return <Unauthorized />
  if (isNotAdmin) return <NotAdmin />
  if (!currentAdmin) return <UnknownError />

  return (
    <ExternalProviderWrappers>
      <AppShell
        isHeaderShown={true}
        searchBar={<OmniSearch />}
        navigatorItems={navigatorItems}
        navigatorAfterItems={<QuickAddButton />}
        headerItems={headerItems}>
        <ScrollToTopOnRouteChange>
          <ErrorWatcher />
          <VersionWatcher />
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Switch>
                <Redirect exact from={Routes.home()} to={Routes.dashboard()} />
                <Route path={Routes.dashboard()} component={Dashboard} />
                <Route path={Routes.account()} component={Account} />
                <Route path={Routes.opportunity()} component={Opportunity} />
                <Route path={Routes.issuer()} component={Issuer} />
                <Route path={Routes.campaignForm()} component={CampaignFormPage} />
                <Route path={Routes.campaign()} component={Campaign} />
                <Route path={Routes.campaigns()} component={Campaigns} />
                <Route path={Routes.orderBook()} component={OrderBook} />
                <Route path={Routes.opportunities()} component={WorkInProgress} />
                <Route path={Routes.accounts()} component={Accounts} exact />
                <Route path={Routes.investorLeads()} component={InvestorLeads} />
                <Route path={Routes.lookerProofOfConcept()} component={LookerProofOfConcept} exact />
                <Route path={Routes.segment()} component={UpdateSegmentFormPage} />
                <Route path={Routes.segmentForm()} component={CreateSegmentFormPage} />
                <Route path={Routes.segments()} component={Segments} exact />
                <Route path={Routes.deals()} component={Deals} />
              </Switch>
            </Suspense>
          </ErrorBoundary>
        </ScrollToTopOnRouteChange>
      </AppShell>
    </ExternalProviderWrappers>
  )
}

const withApollo = (Component: React.FC) => (props: any) => (
  <ApolloProvider client={AdminClient}>
    <Component {...props} />
  </ApolloProvider>
)

export default withApollo(App)
